import { PlusIcon } from "@heroicons/react/20/solid";
import { useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import Button from "~/components/buttons/Button";
import Card from "~/components/Card";
import SearchInput from "~/components/formElements/SearchInput";
import Headline from "~/components/Headline";
import TabBar from "~/components/TabBar";
import { INVOICES_BASE_PATH_WITH_TAB_ID } from "~/constants/appRoutes.ts";
import DeliveryPeriodFilter from "~/modules/billing/components/DeliveryPeriodFilter";
import CreateInvoiceSidebar from "~/modules/billing/components/InvoicePageHeader/components/CreateInvoiceSidebar";
import type { InvoicePageTabId as InvoicePageTabIdType } from "~/modules/billing/types/billingTypes.ts";
import { InvoicePageTabId } from "~/modules/billing/utils/billingUtils.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";
import type { DispatchStateAction } from "~/types/hookTypes.ts";

type Props = {
	activeTabId: InvoicePageTabIdType
	children?: React.ReactNode;
	deliveryPeriodFilter: DateRangeNullable;
	globalFilter: string;
	setDeliveryPeriodFilter: DispatchStateAction<DateRangeNullable>
	setGlobalFilter: (value: string) => void;
	userCanManageInvoices: boolean;
};

const InvoicePageHeader = ({
	activeTabId,
	children,
	deliveryPeriodFilter,
	globalFilter,
	setDeliveryPeriodFilter,
	setGlobalFilter,
	userCanManageInvoices
}: Props) => {
	const [showCreateInvoiceSidebar, setShowCreateInvoiceSidebar] = useState<boolean>(false);
	const navigate = useNavigate();

	const handleTabClick = useCallback((tabId: string) => {
		navigate(generatePath(INVOICES_BASE_PATH_WITH_TAB_ID, { tabId }));
	}, [navigate]);

	const tabs = useMemo(() => {
		return [{
			id: InvoicePageTabId.Pending,
			label: "Offen",
		},
			{
				id: InvoicePageTabId.Draft,
				label: "In Arbeit",
			},
			{
				id: InvoicePageTabId.UNPAID,
				label: "Nicht bezahlt",
			},
			{
				id: InvoicePageTabId.PROCESSED,
				label: "Alle",
			},
			{
				id: InvoicePageTabId.PROCESSED_WITH_ERRORS,
				label: "Fehlerhafte Import aus Excel",
			},
		];
	}, []);

	return <div className="my-4 space-y-4">
		<Card>
			<div className="flex items-center justify-between gap-x-4">
				<Headline type="h2">Rechnungen</Headline>
				<div className="ml-auto flex items-center gap-x-4">
					<div className="w-72">
						<DeliveryPeriodFilter onChange={setDeliveryPeriodFilter}
											  selectedPeriod={deliveryPeriodFilter} />
					</div>
					<div className="w-72">
						<SearchInput value={globalFilter}
									 autoFocus={true}
									 onChange={setGlobalFilter} />
					</div>
				</div>
				{children}
				{userCanManageInvoices && <Button theme="secondary"
						onClick={() => setShowCreateInvoiceSidebar(true)}><PlusIcon className="size-5" /> </Button>}
			</div>
		</Card>
		<TabBar activeTabId={activeTabId}
				tabs={tabs}
				onTabClick={handleTabClick} />
		{userCanManageInvoices && <CreateInvoiceSidebar isOpen={showCreateInvoiceSidebar}
							  onClose={() => setShowCreateInvoiceSidebar(false)} />}
	</div>;
};

export default InvoicePageHeader;